import React, { useEffect, useState, useCallback, Fragment } from 'react';
import { styled } from '@linaria/react';
import { useSelector } from 'react-redux';
import { Button, COLORS } from '@livecommerce/design-system';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { GLOBAL_PRODUCTS, SUPERADMIN_PRODUCT, CLIENT_PRODUCT, DASHBOARD_PRODUCTS } from 'constants/products.js';
import NavigationDropdown from 'components-v3/NavigationDropdown/index.jsx';
import ClientsList from 'components-v3/ClientsList/index.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import useCurrentUserAccess from 'hooks/useCurrentUserAccess.js';
import { useHistory } from 'react-router-dom';
import { GLOBAL, CLIENT_USAGE, buildRoute } from 'constants/routes.js';


const ProductButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  background-color: ${COLORS.dark_500};
  padding: 4px;
  border-radius: 8px;
`;

const StyledClientsList = styled(ClientsList)`
  width: 445px;
`;

const GlobalProductMenu = ({ closeMenu }) => {
  const isSuperadmin = useSelector((state) => state.sessionState.authUser?.isSuperadmin);
  const history = useHistory();
  const { t } = useLocalization();
  const { data, loading: isLoadingClients } = useCurrentUserAccess();
  const [isProductOptionsMenuOpened, setIsProductOptionsMenuOpened] = useState(false);
  const [productList, setProductList] = useState([]);

  useEffect(() => {
    if (isLoadingClients) return;
    const clients = data?.clients;
    const list = [];

    if (Object.keys(clients || {}).length > 0) {
      list.push(DASHBOARD_PRODUCTS[CLIENT_PRODUCT]);
    }

    if (isSuperadmin) {
      list.push(GLOBAL_PRODUCTS.find((product) => product?.id === SUPERADMIN_PRODUCT));
    }
    setProductList(list);
  }, [isSuperadmin, isLoadingClients, data]);

  const selectProduct = (product) => {
    if (product?.id === SUPERADMIN_PRODUCT) {
      closeMenu();
      history.push(GLOBAL);
    }
  };

  const handleProductOptionsMenuClickOutside = () => {
    setIsProductOptionsMenuOpened(false);
  };

  const handleMenuItemClick = useCallback(
    ({ clientId }) => {
      trackingService.track({ eventType: 'client-switcher:switch-client', value: clientId });
      setIsProductOptionsMenuOpened(false);
      closeMenu();
      history.push(buildRoute(CLIENT_USAGE, { clientId }));
    },
    [setIsProductOptionsMenuOpened],
  );

  const handleAdminClick = () => {
    const onlyOneClient = Object.keys(data?.clients || {}).length === 1;

    if (onlyOneClient) {
      handleMenuItemClick({ clientId: Object.keys(data?.clients || {})[0] });
      return;
    }

    setIsProductOptionsMenuOpened((prevValue) => !prevValue);
  };

  if (!productList.length) return null;

  return (
    <ProductButtons>
      {productList.map((product) => (
        <Fragment key={product?.id}>
          {product?.id === CLIENT_PRODUCT && (
            <NavigationDropdown
              key={`${product?.id}-dropdown`}
              isOpened={isProductOptionsMenuOpened}
              onClickOutside={handleProductOptionsMenuClickOutside}
              isControlled
              hoverBackgroundColor="transparent"
              dropdownGap={8}
              dropdownPosition="bottom"
              dropdownAlignment="end"
              dropdownContent={
                <StyledClientsList isOpen handleMenuItemClick={handleMenuItemClick} clients={data?.clients || {}} />
              }
            >
              <Button
                id="open-admin-area-button"
                iconName={product?.icon}
                theme="dark"
                variant="transparent"
                onClick={handleAdminClick}
                key={`${product?.id}-button`}
              >
                {t(product?.translationLabel)}
              </Button>
            </NavigationDropdown>
          )}
          {product?.id !== CLIENT_PRODUCT && (
            <Button
              id="open-superadmin-button"
              iconName={product?.icon}
              theme="dark"
              variant="transparent"
              onClick={() => selectProduct(product)}
              key={`${product?.id}-button`}
            >
              {t(product?.translationLabel)}
            </Button>
          )}
        </Fragment>
      ))}
    </ProductButtons>
  );
};

export default GlobalProductMenu;
